"use client";

import { motion, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import Link from "next/link";
import { useRef, useState } from "react";
import {
  useAppScroll,
  useElementScrollProgress,
} from "@/components/providers/locomotive-scroll-provider";
import { DemoTradeWidget } from "@/components/landing/demo-trade-widget";
import { PortalCtaButton } from "@/components/landing/portal-cta-button";
import { AssetIcon } from "@/components/ui/asset-icon";
import { Button } from "@/components/ui/button";
import { PopText } from "@/components/motion/reveal";
import { GradientText } from "@/components/ui/gradient-text";
import { useMotionPreference } from "@/hooks/use-motion-preference";
import { CRYPTO, STOCKS } from "@/lib/landing-data";

const MARKET_CHIPS = [CRYPTO.BTC, STOCKS.NVDA, CRYPTO.SOL, STOCKS.TSLA, CRYPTO.ETH, STOCKS.AAPL];

const EASE = [0.22, 1, 0.36, 1] as const;

// `custom` is the start delay in seconds, so the headline can pop first and the
// supporting content cascade in after it.
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.6, ease: EASE },
  }),
};

export function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollTo } = useAppScroll();
  const { isMobile, reducedMotion } = useMotionPreference();
  const [seeHowItWorksActive, setSeeHowItWorksActive] = useState(false);
  // Local progress from the global Locomotive tick — no useScroll container.
  const progress = useElementScrollProgress(sectionRef, "exit");

  // On mobile the widget stacks at the bottom of a tall hero, so a [0,0.6] fade
  // hides it before you've even scrolled to it. Keep content fully visible
  // through most of the scroll and only fade as it leaves the viewport.
  const contentOpacity = useTransform(
    progress,
    isMobile ? [0, 0.85, 1] : [0, 0.6],
    isMobile ? [1, 1, 0] : [1, 0],
  );
  const contentY = useTransform(progress, [0, 0.6], [0, isMobile ? -16 : -50]);
  const cueOpacity = useTransform(progress, [0, 0.15], [1, 0]);

  const scrollToNext = () => scrollTo("#cross-chain", { offset: -80 });
  const drawArrow = seeHowItWorksActive && !reducedMotion;

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative flex min-h-[100svh] w-full items-center justify-center overflow-hidden px-4 pb-16 pt-28"
    >
      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 mx-auto grid w-full max-w-6xl items-center gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10"
      >
        <motion.div initial="hidden" animate="visible" className="text-center lg:text-left">
 

          <h1 className="mt-5 text-balance text-5xl font-semibold leading-[1.1] tracking-tight sm:text-6xl xl:text-7xl">
            <PopText className="block" align="left" delay={0.3}>
              Every market.
            </PopText>
            <PopText className="block" align="left" delay={0.85}>
              <GradientText shimmer>One gate.</GradientText>
            </PopText>
          </h1>

          <motion.p
            custom={1.15}
            variants={fadeUp}
            className="mx-auto mt-5 hidden max-w-xl text-pretty text-lg text-muted-foreground md:block lg:mx-0"
          >
            Buy any token or stock on any chain with a card. Swap cross-chain with no bridges.
            Trade prediction markets and narrative baskets — all from one wallet, no seed phrases or
            extensions.
          </motion.p>

          <motion.div
            custom={1.3}
            variants={fadeUp}
            className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center lg:justify-start"
          >
            <PortalCtaButton className="w-full sm:w-auto" />
            <Button
              variant="outline"
              size="lg"
              className="group relative overflow-hidden w-full dark:hover:bg-background dark:hover:text-foreground sm:w-auto"
              onBlur={() => setSeeHowItWorksActive(false)}
              onClick={scrollToNext}
              onFocus={() => setSeeHowItWorksActive(true)}
              onMouseEnter={() => setSeeHowItWorksActive(true)}
              onMouseLeave={() => setSeeHowItWorksActive(false)}
            >
              <span className="relative transition-opacity duration-200 group-hover:opacity-0 group-focus-visible:opacity-0">
                See how it works
              </span>
              <motion.svg
                key={drawArrow ? "active" : "rest"}
                aria-hidden
                viewBox="0 0 24 24"
                className="absolute inset-0 m-auto size-5 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.25}
              >
                <motion.path
                  d="M12 4v15"
                  initial={drawArrow ? { pathLength: 0 } : false}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: reducedMotion ? 0 : 0.35, ease: EASE }}
                />
                <motion.path
                  d="m6 13 6 6 6-6"
                  initial={drawArrow ? { pathLength: 0 } : false}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: reducedMotion ? 0 : 0.35, delay: reducedMotion ? 0 : 0.18, ease: EASE }}
                />
              </motion.svg>
            </Button>
          </motion.div>

          <motion.div
            custom={1.45}
            variants={fadeUp}
            className="mt-8 flex items-center justify-center gap-3 lg:justify-start"
          >
            <div className="flex -space-x-2">
              {MARKET_CHIPS.map((a) => (
                <AssetIcon key={a.symbol} asset={a} size={30} className="ring-2 ring-background" />
              ))}
            </div>
            <span className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">12,000+</span> assets · 20+ chains
            </span>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40, rotateX: 8 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto w-full max-w-sm [perspective:1200px] lg:mx-0 lg:justify-self-end"
        >
          <div className="motion-safe:animate-float">
            <DemoTradeWidget />
          </div>
        </motion.div>
      </motion.div>

      <motion.button
        style={{ opacity: cueOpacity }}
        onClick={scrollToNext}
        aria-label="Scroll to explore"
        className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-1 text-muted-foreground"
      >
        <span className="text-xs">Scroll to explore</span>
        <ChevronDown className="size-4 motion-safe:animate-bounce" />
      </motion.button>
    </section>
  );
}
